import React, { useState, useEffect } from 'react';
import { X, Check, RefreshCw, AlertCircle, AlertTriangle, Layers } from 'lucide-react';
import { playPulseRefineDrill } from '../../utils/pulseAudio';

export interface RefineTargetField {
  sectionKey: string;
  sectionTitle: string;
  currentText: string;
}

export function clinicalRefineRequestBody(input: {
  docType: string;
  mode: 'SINGLE' | 'PAIRED';
  fields: Array<{ sectionKey: string; currentText: string }>;
  instruction: string;
  clientName?: string;
  clientId?: string;
  reportingPeriod?: string;
}) {
  return {
    docType: input.docType,
    mode: input.mode,
    fields: input.fields,
    sectionKey: input.fields[0]?.sectionKey,
    currentText: input.fields[0]?.currentText,
    instruction: input.instruction,
    clientName: input.clientName,
    clientId: input.clientId,
    reportingPeriod: input.reportingPeriod
  };
}

export interface AiRefineModalProps {
  isOpen: boolean;
  onClose: () => void;
  docType: string;
  clientName: string;
  clientId?: string;
  reportingPeriod?: string;
  // Single-field props (backward compatibility)
  sectionKey?: string;
  sectionTitle?: string;
  currentText?: string;
  onApply?: (newText: string) => void;
  // Paired-fields props
  pairedFields?: [RefineTargetField, RefineTargetField];
  initialMode?: 'SINGLE' | 'PAIRED';
  onApplyPaired?: (updates: Record<string, string>) => void;
  onApplyField?: (sectionKey: string, newText: string) => void;
}

const QUICK_PROMPTS = [
  'Make more assertive for Medicaid medical necessity',
  'Expand functional daily routine carryover examples',
  'Incorporate caregiver feedback and family routines',
  'Highlight progress and responsiveness to multimodal cueing',
  'Make concise, objective, and professional',
  'Ensure measurable baseline numbers and objective metrics'
];

export const AiRefineModal: React.FC<AiRefineModalProps> = ({
  isOpen,
  onClose,
  docType,
  clientName,
  clientId,
  reportingPeriod,
  sectionKey: initialSectionKey,
  sectionTitle: initialSectionTitle,
  currentText: initialCurrentText,
  onApply,
  pairedFields,
  initialMode,
  onApplyPaired,
  onApplyField
}) => {
  const hasPairs = Boolean(pairedFields && pairedFields.length === 2);

  const [activeMode, setActiveMode] = useState<'SINGLE' | 'PAIRED'>(() => {
    if (initialMode) return initialMode;
    return hasPairs ? 'PAIRED' : 'SINGLE';
  });

  const [activeSingleIndex, setActiveSingleIndex] = useState<number>(() => {
    if (hasPairs && pairedFields && initialSectionKey) {
      const idx = pairedFields.findIndex(f => f.sectionKey === initialSectionKey);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });

  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState<Record<string, { refinedText: string; explanation: string }>>({});
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Sync state on open
  useEffect(() => {
    if (isOpen) {
      setInstruction('');
      setProposals({});
      setWarnings([]);
      setError(null);
      if (initialMode) {
        setActiveMode(initialMode);
      } else {
        setActiveMode(hasPairs ? 'PAIRED' : 'SINGLE');
      }
      if (hasPairs && pairedFields && initialSectionKey) {
        const idx = pairedFields.findIndex(f => f.sectionKey === initialSectionKey);
        setActiveSingleIndex(idx >= 0 ? idx : 0);
      }
    }
  }, [isOpen, initialSectionKey, hasPairs, initialMode]);

  if (!isOpen) return null;

  // Resolve target fields for the current mode
  const activeFields: RefineTargetField[] = (() => {
    if (activeMode === 'PAIRED' && hasPairs && pairedFields) {
      return pairedFields;
    }
    if (hasPairs && pairedFields) {
      return [pairedFields[activeSingleIndex]];
    }
    return [{
      sectionKey: initialSectionKey || 'section',
      sectionTitle: initialSectionTitle || 'Section',
      currentText: initialCurrentText || ''
    }];
  })();

  const handleRefine = async (customInstruction?: string) => {
    const promptToUse = customInstruction || instruction;
    if (!promptToUse.trim()) return;

    playPulseRefineDrill();
    setLoading(true);
    setError(null);
    try {
      const payloadFields = activeFields.map(f => ({
        sectionKey: f.sectionKey,
        currentText: proposals[f.sectionKey]?.refinedText || f.currentText
      }));

      const res = await fetch('/api/clinical/docs/ai-refine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clinicalRefineRequestBody({
          docType,
          mode: activeMode,
          fields: payloadFields,
          instruction: promptToUse,
          clientName,
          clientId,
          reportingPeriod
        }))
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Refinement failed.');
      }

      const data = await res.json();
      if (data.proposals && typeof data.proposals === 'object') {
        setProposals(data.proposals);
      } else if (data.refinedText) {
        setProposals({
          [payloadFields[0]?.sectionKey || 'section']: {
            refinedText: data.refinedText,
            explanation: data.explanation || ''
          }
        });
      }
      if (Array.isArray(data.insufficientEvidenceWarnings)) {
        setWarnings(data.insufficientEvidenceWarnings);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during refinement.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplySingle = (field: RefineTargetField) => {
    const proposal = proposals[field.sectionKey];
    if (proposal && proposal.refinedText) {
      if (onApplyField) {
        onApplyField(field.sectionKey, proposal.refinedText);
      } else if (onApply) {
        onApply(proposal.refinedText);
      }
    }
    onClose();
  };

  const handleApplyAll = () => {
    if (activeMode === 'PAIRED' && hasPairs && pairedFields) {
      const updates: Record<string, string> = {};
      for (const f of pairedFields) {
        if (proposals[f.sectionKey]?.refinedText) {
          updates[f.sectionKey] = proposals[f.sectionKey].refinedText;
        }
      }
      if (onApplyPaired) {
        onApplyPaired(updates);
      } else if (onApplyField) {
        Object.entries(updates).forEach(([k, v]) => onApplyField(k, v));
      }
    } else {
      const f = activeFields[0];
      const p = proposals[f.sectionKey];
      if (p?.refinedText) {
        if (onApplyField) onApplyField(f.sectionKey, p.refinedText);
        else if (onApply) onApply(p.refinedText);
      }
    }
    onClose();
  };

  const hasAnyProposal = activeFields.some(f => Boolean(proposals[f.sectionKey]?.refinedText));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden text-slate-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center">
              <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-5 h-5 shrink-0 object-contain" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-sm font-bold text-white">Section Refinement</h2>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">
                  {docType}
                </span>
                {activeMode === 'PAIRED' && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 flex items-center space-x-1">
                    <Layers className="w-3 h-3 inline mr-1" />
                    <span>Paired Fields</span>
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Targeting client: <strong className="text-slate-200">{clientName}</strong> • Grounded in documented chart records
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Mode toggle if pairs are available */}
            {hasPairs && pairedFields && (
              <div className="flex bg-slate-800/80 p-0.5 rounded-lg border border-slate-700 text-[11px]">
                <button
                  type="button"
                  onClick={() => setActiveMode('PAIRED')}
                  className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                    activeMode === 'PAIRED'
                      ? 'bg-sky-600 text-white shadow-sm'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Paired ({pairedFields[0].sectionTitle} + {pairedFields[1].sectionTitle})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveMode('SINGLE')}
                  className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                    activeMode === 'SINGLE'
                      ? 'bg-sky-600 text-white shadow-sm'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Single Field
                </button>
              </div>
            )}

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mode Tab selector when in Single mode with pairs */}
        {hasPairs && pairedFields && activeMode === 'SINGLE' && (
          <div className="px-5 py-2 bg-slate-950/40 border-b border-slate-800/80 flex items-center space-x-2 text-xs">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Select Field:</span>
            {pairedFields.map((f, idx) => (
              <button
                key={f.sectionKey}
                type="button"
                onClick={() => setActiveSingleIndex(idx)}
                className={`px-3 py-1 rounded-md text-[11px] font-semibold transition-all ${
                  activeSingleIndex === idx
                    ? 'bg-slate-700 text-white border border-slate-600'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}
              >
                {f.sectionTitle}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto text-xs flex-1">
          {/* Quick Prompts */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Quick Suggestions for Speech Pathology
            </span>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_PROMPTS.map((qp, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setInstruction(qp);
                    handleRefine(qp);
                  }}
                  disabled={loading}
                  className="px-2.5 py-1 rounded-md bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-sky-300 border border-slate-700/60 text-[11px] transition-all disabled:opacity-50 cursor-pointer"
                >
                  {qp}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Instruction Input */}
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Clinician Directive
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRefine();
                  }
                }}
                placeholder="e.g., Emphasize functional medical necessity and daily communication needs..."
                className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
              />
              <button
                type="button"
                onClick={() => handleRefine()}
                disabled={loading || !instruction.trim()}
                className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white text-xs font-bold flex items-center space-x-1.5 shadow-md shadow-sky-900/30 transition-all cursor-pointer border border-sky-400/30 active:scale-[0.98] disabled:opacity-40 shrink-0"
              >
                {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4 h-4 shrink-0 object-contain" />}
                <span>{loading ? 'Refining...' : 'Refine'}</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-lg text-xs flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Insufficient Evidence Warning Banner */}
          {warnings.length > 0 && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg text-xs space-y-1">
              <div className="flex items-center space-x-1.5 font-bold text-amber-400">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Documentation Evidence Warning</span>
              </div>
              <ul className="list-disc list-inside space-y-0.5 text-[11px] text-amber-200/90 pl-1">
                {warnings.map((w, idx) => (
                  <li key={idx}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Render Active Fields Comparison (Single or Paired) */}
          <div className="space-y-4">
            {activeFields.map((field) => {
              const proposal = proposals[field.sectionKey];
              return (
                <div key={field.sectionKey} className="border border-slate-800 rounded-xl p-3.5 bg-slate-950/40 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                    <span className="font-bold text-slate-200 text-xs flex items-center space-x-2">
                      <span className="w-2 h-2 rounded-full bg-sky-400" />
                      <span>{field.sectionTitle}</span>
                    </span>
                    {activeMode === 'PAIRED' && proposal?.refinedText && (
                      <button
                        type="button"
                        onClick={() => handleApplySingle(field)}
                        className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] font-bold text-sky-300 border border-slate-700 hover:border-sky-500/50 transition-all cursor-pointer"
                      >
                        Apply {field.sectionTitle} Only
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Current Draft */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                        <span>Current Draft</span>
                        <span>{(field.currentText || '').length} chars</span>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-950/80 border border-slate-800 text-slate-300 min-h-[100px] max-h-[180px] overflow-y-auto leading-relaxed whitespace-pre-wrap text-[11px]">
                        {field.currentText || <span className="italic text-slate-600">Field is currently empty.</span>}
                      </div>
                    </div>

                    {/* Proposed Refinement */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] text-sky-400 font-semibold uppercase tracking-wider">
                        <span className="flex items-center space-x-1.5">
                          <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4 h-4 shrink-0 object-contain" />
                          <span>Proposed Refinement</span>
                        </span>
                        {proposal?.refinedText && (
                          <span className="text-[10px] text-emerald-400 font-mono">
                            Ready
                          </span>
                        )}
                      </div>
                      <div className="p-3 rounded-lg bg-sky-950/20 border border-sky-500/30 text-slate-100 min-h-[100px] max-h-[180px] overflow-y-auto leading-relaxed whitespace-pre-wrap text-[11px]">
                        {proposal?.refinedText ? (
                          proposal.refinedText
                        ) : loading ? (
                          <div className="flex items-center justify-center h-full text-slate-500 space-x-2 py-6">
                            <RefreshCw className="w-4 h-4 animate-spin text-sky-400" />
                            <span>Formulating grounded clinical proposal...</span>
                          </div>
                        ) : (
                          <span className="italic text-slate-500">
                            Click a suggestion above or submit a directive to review proposed text.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Proposal Explanation */}
                  {proposal?.explanation && (
                    <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-[11px] leading-relaxed">
                      <span className="font-bold text-sky-400">Clinical Rationale: </span>
                      {proposal.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-slate-800 bg-slate-950/70 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs transition-colors cursor-pointer"
          >
            Cancel
          </button>

          <div className="flex items-center space-x-2">
            {activeMode === 'PAIRED' && hasPairs && pairedFields && (
              <>
                {proposals[pairedFields[0].sectionKey]?.refinedText && (
                  <button
                    type="button"
                    onClick={() => handleApplySingle(pairedFields[0])}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition-colors cursor-pointer"
                  >
                    Apply {pairedFields[0].sectionTitle} Only
                  </button>
                )}
                {proposals[pairedFields[1].sectionKey]?.refinedText && (
                  <button
                    type="button"
                    onClick={() => handleApplySingle(pairedFields[1])}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition-colors cursor-pointer"
                  >
                    Apply {pairedFields[1].sectionTitle} Only
                  </button>
                )}
              </>
            )}

            <button
              type="button"
              onClick={handleApplyAll}
              disabled={!hasAnyProposal}
              className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold text-xs flex items-center space-x-1.5 transition-colors shadow-md shadow-emerald-600/20 cursor-pointer whitespace-nowrap shrink-0"
            >
              <Check className="w-4 h-4" />
              <span className="sm:hidden">Apply</span>
              <span className="hidden sm:inline">
                {activeMode === 'PAIRED' && hasPairs && pairedFields
                  ? `Apply All (${pairedFields[0].sectionTitle} + ${pairedFields[1].sectionTitle})`
                  : `Apply Refinement to ${activeFields[0]?.sectionTitle}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
